/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import AssertionError from 'assertion-error';
import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './playback-controls.html';

import { $root } from '../../player/player';
import CountdownRoot from '../countdown/countdown-root';

const inactivityTimeout = 5000;
const activityEvents = [
  'keydown',
  'mousemove',
  'click',
  'touchstart',
  'touchmove',
  'pointerdown',
  'pointermove',
  'focusin'
];
const minSpaceRatio = 1.2;

export default defineComponent({
  name: 'PlaybackControls',
  template,
  components: { CountdownRoot },
  created() {
    this.onActivity = this.onActivity.bind(this);
    this.onResize = this.onResize.bind(this);
  },
  destroyed() {
    const targetElement = this.$el.parentElement;
    if (!targetElement) {
      throw new AssertionError('Missing parent element for playback controls');
    }
    activityEvents.forEach((eventName) => {
      targetElement.removeEventListener(eventName, this.onActivity);
    });
    window.removeEventListener('resize', this.onResize);
  },
  mounted() {
    const targetElement = this.$el.parentElement;
    if (!targetElement) {
      throw new AssertionError('Missing parent element for playback controls');
    }
    activityEvents.forEach((eventName) => {
      targetElement.addEventListener(eventName, this.onActivity);
    });
    window.addEventListener('resize', this.onResize);
    this.setOverlapping();
    this.onResize();
    this.$nextTick(() => {
      this.isInitializing = false;
    });
  },
  computed: {
    visible() {
      return this.isActive && !this.isTooSmall && !this.isOverlapping;
    }
  },
  data() {
    return {
      inactivityThreshold: getTimeoutTimestamp(),
      /** @type {boolean} */
      isActive: true,
      /** @type {boolean} */
      isTooSmall: false,
      /** @type {boolean} */
      isOverlapping: false,
      /** @type {boolean} */
      isInitializing: true
    };
  },
  methods: {
    // TODO: S Vue 3 to bude zrejme zbytočné, keď sa použije na content-overlay
    // <teleport> a CSS by malo automaticky pomocou priradeného z-indexu v tom
    // istom podstrome vyriešiť prekrytie.
    setOverlapping() {
      const el = /** @type {HTMLElement} */ (this.$el);
      if (!el || el.offsetWidth === 0) return false;

      const rect = el.getBoundingClientRect();
      const points = [
        // center
        [rect.left + rect.width / 2, rect.top + rect.height / 2],
        // top-left
        [rect.left + 1, rect.top + 1],
        // top-right
        [rect.right - 1, rect.top + 1],
        // bottom-left
        [rect.left + 1, rect.bottom - 1],
        // bottom-right
        [rect.right - 1, rect.bottom - 1]
      ];

      for (const [x, y] of points) {
        const elementAtPoint = document.elementFromPoint(x, y);
        if (!(elementAtPoint?.closest('div')?.id === el.parentElement?.id)) {
          this.isOverlapping = true;
          return;
        }
      }
      this.isOverlapping = false;
    },

    setTooSmall() {
      const el = /** @type {HTMLElement} */ (this.$el);
      const parent = /** @type {HTMLElement} */ (this.$el.parentElement);

      const elementWidth = el.offsetWidth;
      const elementHeight = el.offsetHeight;
      const targetWidth = parent.offsetWidth;
      const targetHeight = parent.offsetHeight;

      const minRequiredWidth = elementWidth * minSpaceRatio;
      const minRequiredHeight = elementHeight * minSpaceRatio;

      this.isTooSmall =
        targetWidth < minRequiredWidth || targetHeight < minRequiredHeight;
    },
    onActivity() {
      this.isActive = true;
      this.inactivityThreshold = getTimeoutTimestamp();
    },
    onInactivity() {
      this.isActive = false;
    },
    onStop() {
      $root(this).playbackControlsEmitter.stop();
    },
    onResize() {
      this.setTooSmall();
    },
    onskip() {
      $root(this).playbackControlsEmitter.skip(this.content);
    }
  },
  props: {
    content: { /** @type {PropType<Content>} */ type: Object, required: true }
  },
  emits: {}
});

function getTimeoutTimestamp() {
  return new Date().getTime() + inactivityTimeout;
}
